import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
  getPrivateLink,
  requestPrivateLinkInvite,
} from '../lib/tycho';

import {
  AlertBar,
  Base,
  Form,
  Heading,
  Paragraph,
  Link,
  Platform,
  TextInput,
  TextInputButton,
} from '@brandwatch/axiom-components';

import './PrivateLink.css';

const PrivateLinkLayout = ({children}) => {
  return (
    <Platform theme="night">
      <div className="PrivateLink">
        <div className="PrivateLink__hero" />
        <div className="PrivateLink__body">
          <div className="PrivateLink__header">
            <div className="PrivateLink__deckImg" />
          </div>
          <div className="PrivateLink__content">
            {children}
          </div>
        </div>
      </div>
    </Platform>
  );
};

PrivateLinkLayout.propTypes = {
  children: PropTypes.any
};

/**
 * Renders the page that shows when the user tries to access a private link
 * but doesn't have access yet. It allows the user to submit their email
 * address and receive an email invite to the deck (so long as their email
 * address is whitelisted).
 */
const PrivateLinkRequestInvite = ({
  privateLinkIdHash,
}) => {
  const [privateLink, setPrivateLink] = useState();
  const [email, setEmail] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUnexpectedError, setShowUnexpectedError] = useState(false);
  const [invalidInputError, setInvalidInputError] = useState(null);
  const [showUnavailable, setShowUnavailable] = useState(false);

  // Request basic information about the private link.
  // This is needed to know whether the private link exists / is enabled,
  // and so that the deck's name can be displayed.
  useEffect(() => {
    getPrivateLink(privateLinkIdHash)
      .then(privateLink => {
        setPrivateLink(privateLink);

        if (!privateLink.enabled) {
          setShowUnavailable(true);
        }
      })
      .catch(err => {
        if (err.res && err.res.status === 404) {
          setShowUnavailable(true);
        } else {
          setShowUnexpectedError(true);
        }
      });
  }, [privateLinkIdHash]);

  // Updates the email address as the user types.
  const handleOnEmailChange = useCallback(evt => {
    setEmail(evt.target.value);
  }, [setEmail]);

  // Handles the user clicking the "check email" button.
  // Submits the user's email address and requests an email invite.
  const handleOnSubmit = useCallback(evt => {
    evt.preventDefault();

    if (!email.trim().length) {
      setInvalidInputError('Please enter your email address');
      return;
    }

    requestPrivateLinkInvite(privateLinkIdHash, email)
      .then(() => {
        setShowConfirmation(true);
      })
      .catch(err => {
        const unexpectedErrorMessage = 'Unexpected error requesting private link invite.';

        if (err.res) {
          err.res.json().then((body => {
            const errorId = body.error && body.error.id;

            if (errorId === 'invalid_email') {
              setInvalidInputError('Please enter a valid email address');
            } else if (err.res.status === 403) {
              setShowConfirmation(true);
            } else if (err.res.status === 404) {
              setShowUnavailable(true);
            } else {
              setShowUnexpectedError(true);
              console.error(unexpectedErrorMessage, err); // eslint-disable-line no-console
            }
          }));
        } else {
          setShowUnexpectedError(true);
          console.error(unexpectedErrorMessage, err); // eslint-disable-line no-console
        }
      });
  }, [
    email,
    privateLinkIdHash,
  ]);

  // Handles the user clicking the "use another email" link, after they've entered an
  // email address and requested an email invite.
  const handleUseAnotherEmail = useCallback(() => {
    setEmail('');
    setInvalidInputError(null);
    setShowConfirmation(false);
  }, [
    setEmail,
    setShowConfirmation,
  ]);

  if (showUnexpectedError) {
    return (
      <PrivateLinkLayout>
        <Heading
          space="x6"
          textSize="headline"
          textStrong={true}
        >
          The private link is unavailable
        </Heading>
        <Heading
          textColor="subtle"
          textSize="large"
        >
          Sorry, there was an unexpected error. Please try again later.
        </Heading>
      </PrivateLinkLayout>
    );
  }

  /**
   * The private link is unavailable when it 404s. Possible reasons:
   *
   * - The URL (which includes the hash of the private link's ID) might be wrong.
   * - The private link might've been disabled.
   * - The private link might have no email domains whitelisted.
   */
  if (showUnavailable) {
    return (
      <PrivateLinkLayout>
        <Heading
          space="x6"
          textSize="headline"
          textStrong={true}
        >
          The private link is unavailable
        </Heading>
        <Heading
          textColor="subtle"
          textSize="large"
        >
          Whoever setup this private link has either turned it off or not added your email domain. Contact them for access.
        </Heading>
      </PrivateLinkLayout>
    );
  }

  // The absence of errors by this point means that the private link is loading.
  if (!privateLink) {
    return null;
  }

  // Either ask the user for the email address, or let them know that an email has been sent.
  return (
    <PrivateLinkLayout>
      <Heading
        textSize="headline"
        space="x6"
        textStrong={true}
      >
        {privateLink.deck_name}
      </Heading>
      {showConfirmation ? (
        <>
          <Heading
            textSize="headtitle"
            space="x3"
          >
            Once we have verified your email, we&apos;ll send the private link to your inbox.
          </Heading>
          <Heading
            textSize="large"
            textColor="subtle"
          >
            Not received the link yet? You&apos;ll need to contact whoever sent the private link to make sure you have access.
          </Heading>
          <Base space="x6">
            <Link
              href="#"
              onClick={handleUseAnotherEmail}
              textStrong={true}
            >
              Use another email
            </Link>
          </Base>
        </>
      ) : (
        <>
          <Heading
            textSize="headtitle"
            space="x3"
          >
            We need to verify your email before you can use this link.
          </Heading>
          <Heading
            textSize="large"
            space="x4"
            textColor="subtle"
          >
            It only takes a few seconds. Then check your email inbox for a secure link from us.
          </Heading>
          <Form onSubmit={handleOnSubmit}>
            {invalidInputError && (
              <AlertBar type="error">
                {invalidInputError}
              </AlertBar>
            )}
            <TextInput
              invalid={!!invalidInputError}
              onChange={handleOnEmailChange}
              placeholder="Enter business email here"
              size="large"
              value={email}
            >
              <TextInputButton
                variant="primary"
                type="submit"
              >
                Verify Email
              </TextInputButton>
            </TextInput>
          </Form>
          <Heading
            textSize="large"
            textStrong={true}
          >
            Why do we verify your email?
          </Heading>
          <Paragraph>Whoever set up this private link will have added certain email domains (the bit after the @). Only those emails can use the private link - we need to make sure yours is one of them.</Paragraph>
          <Paragraph><b>Don&apos;t worry, we won&apos;t use your email for anything else.</b></Paragraph>
        </>
      )}
    </PrivateLinkLayout>
  );
};

PrivateLinkRequestInvite.propTypes = {
  privateLinkIdHash: PropTypes.string,
};

export default PrivateLinkRequestInvite;
